import { HttpEvent, HttpEventType, HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, OnInit } from '@angular/core';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { NgbActiveModal, NgbTooltip, NgbProgressbar } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { FileService } from '../file.service';
import { AlertService } from '../alert.service';
import heic2any from "heic2any";
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgIf, NgFor, NgClass } from '@angular/common';

@Component({
    selector: 'app-file-upload-modal',
    templateUrl: './file-upload-modal.component.html',
    styleUrls: ['./file-upload-modal.component.css'],
    imports: [NgIf, NgFor, NgbTooltip, FaIconComponent, NgbProgressbar]
})
export class FileUploadModalComponent implements OnInit{

  // Properties:
  getPresignedURL: (data: any) => Promise<string>;
  completed: EventEmitter<void> = new EventEmitter<void>();

  // Files
  files: File[] = [];
  uploadTotal: number = 0;
  uploadLoaded: number = 0;
  lastUploadLoaded: number = 0;
  uploadSubscription: Subscription;
  isDarkModeEnabled: boolean = false;

  // Font Awesome Properties
  faTimes = faTimes;

  constructor(
    private fileService: FileService,
    public modal: NgbActiveModal, private alertService: AlertService) { }

  ngOnInit(): void {
    this.isDarkModeEnabled = localStorage.getItem('isDarkModeEnabled') === 'true';
  }

  // Browse Files
  browseFiles(): void {
    document.getElementById('FILE_INPUT').click();
  }

  // File Input Changed
  async fileInputChanged(event: any): Promise<void> {
    const selectedFiles: FileList = event;
    if (!selectedFiles) return;

    const convertedFiles: File[] = [];

    for (const file of Array.from(selectedFiles)) {
      if (!this.isValidFilename(file.name)) {
        this.alertService.showErrorAlert(
          "<br/>Filename is invalid. <br/> Only alphanumeric characters, <br/> hyphens, dashes, underscores, dots, <br/> parenthesis, and spaces are allowed.",
          'uploading-msg'
        );
        return;
      }

      // Check if the file is HEIC and convert it
      if (file.type === 'image/heic' || file.name.toLowerCase().endsWith('.heic')) {
        try {
          const convertedBlob = await heic2any({ blob: file, toType: 'image/jpeg' }) as Blob;
          const newFile = new File([convertedBlob], file.name.replace('.heic', '.jpg'), { type: 'image/jpeg' });
          convertedFiles.push(newFile);
        } catch (error) {
          this.alertService.showErrorAlert("Error converting HEIC file.", 'uploading-msg');
        }
      } else {
        convertedFiles.push(file);
      }
    }

    // Append converted files to upload list
    this.files = [...this.files, ...convertedFiles];
  }

  // Remove File
  removeFile(index: number) {
    this.files.splice(index, 1);
  }

  // Upload Files
  async uploadFiles() {
    try {
      const fileNameElements = document.getElementsByName('FILE_NAME');
      this.uploadTotal = this.files.reduce((acc, file) => acc + file.size, 0);

      for (let i = 0; i < this.files.length; i++) {
        this.lastUploadLoaded = this.uploadLoaded;
        const data: any = {
          name: (<HTMLInputElement>fileNameElements[i]).value,
          type: this.files[i].type || 'image/jpeg',
          size: this.files[i].size
        };

        const presignedUrl = await this.getPresignedURL(data);
        await new Promise<void>((resolve, reject) => {
          this.uploadSubscription = this.fileService.uploadFileDirectToS3(presignedUrl, this.files[i]).subscribe((event: HttpEvent<any>) => {
            if (event.type == HttpEventType.UploadProgress) {
              this.uploadLoaded = event.loaded + this.lastUploadLoaded;
            }
            if (event.type == HttpEventType.Response) {
              (event.ok) ? resolve() : reject();
            }
          });
        });
      }

      this.modal.close();
      this.resetUploads();
      this.completed.emit();
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        if (error.error.category == 'QUOTA_REACHED') this.resetUploads(false);
      }
    }
  }

  // Cancel Uploads
  cancelUploads(): void {
    if (this.uploadSubscription) this.uploadSubscription.unsubscribe();
    this.modal.dismiss();
    this.resetUploads();
    this.completed.emit();
  }

  // Reset Uploads
  private resetUploads(resetFiles: boolean = true): void {
    this.modal.close();
    if (resetFiles) this.files.length = 0;
    this.uploadTotal = 0;
    this.uploadLoaded = 0;
    this.lastUploadLoaded = 0;
  }

  // Validate filename using regex pattern
  isValidFilename(filename: string): boolean {
    filename = filename.replace(/[\u00A0\u202F]/g, ' ');
    const pattern = /^[a-zA-Z0-9\-_.() ]+$/;
    return pattern.test(filename);
  }
}
